/**
 * Issue/Risk Register Template Form
 * Issue and risk tracking
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const IssueRiskRegisterForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    issues: [{ id: '', title: '', description: '', category: '', priority: 'Medium', status: 'Open', owner: '', reported_date: '', target_date: '', resolution: '', impact: '', notes: '' }],
    risks: [{ id: '', title: '', description: '', category: '', likelihood: 'Medium', impact: 'Medium', risk_score: 0, status: 'Open', owner: '', mitigation: '', contingency: '', trigger: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Project Manager',
    issues: [
      { id: 'ISS-001', title: 'Third-party API Rate Limits', description: 'Payment gateway API rate limits causing checkout failures during peak hours', category: 'Technical', priority: 'Critical', status: 'In Progress', owner: 'Tech Lead', reported_date: '2025-01-10', target_date: '2025-01-20', resolution: 'Implementing request queuing and caching', impact: 'High - affects 5% of checkout attempts', notes: '' },
      { id: 'ISS-002', title: 'Resource Shortage - QA', description: 'QA team understaffed for regression testing', category: 'Resource', priority: 'High', status: 'Open', owner: 'QA Manager', reported_date: '2025-01-08', target_date: '2025-01-25', resolution: '', impact: 'Medium - may delay testing phase', notes: 'Requested contractor support' },
      { id: 'ISS-003', title: 'Legacy Data Migration Errors', description: 'Customer address data has inconsistent formatting', category: 'Data', priority: 'Medium', status: 'Resolved', owner: 'DBA', reported_date: '2025-01-05', target_date: '2025-01-12', resolution: 'Created data cleansing scripts', impact: 'Low - affects display only', notes: 'Closed on 2025-01-11' }
    ],
    risks: [
      { id: 'RISK-001', title: 'Key Developer Departure', description: 'Senior developer with domain knowledge considering other opportunities', category: 'Resource', likelihood: 'Medium', impact: 'High', risk_score: 12, status: 'Monitoring', owner: 'Engineering Manager', mitigation: 'Knowledge transfer sessions, documentation, retention bonus', contingency: 'Cross-training backup resources', trigger: 'Developer gives notice', notes: '' },
      { id: 'RISK-002', title: 'Scope Creep', description: 'Stakeholders requesting additional features beyond MVP', category: 'Scope', likelihood: 'High', impact: 'Medium', risk_score: 12, status: 'Active', owner: 'Product Manager', mitigation: 'Strict change control process, backlog prioritization', contingency: 'Phase 2 roadmap for deferred features', trigger: 'New feature requests exceed buffer', notes: 'Already happened twice' },
      { id: 'RISK-003', title: 'Performance Issues at Scale', description: 'Application may not handle projected Black Friday traffic', category: 'Technical', likelihood: 'Medium', impact: 'Critical', risk_score: 15, status: 'Active', owner: 'Architect', mitigation: 'Load testing, auto-scaling, CDN implementation', contingency: 'Emergency scaling plan, traffic throttling', trigger: 'Load test failures', notes: 'Load testing scheduled for Feb' },
      { id: 'RISK-004', title: 'Regulatory Compliance Gap', description: 'New GDPR requirements may require additional features', category: 'Compliance', likelihood: 'Low', impact: 'High', risk_score: 8, status: 'Monitoring', owner: 'Legal/Compliance', mitigation: 'Legal review of requirements, compliance audit', contingency: 'Rapid implementation team on standby', trigger: 'Legal advisory on new requirements', notes: '' }
    ],
    rai_assignments: [
      { task: 'Weekly risk review meeting', responsible: 'Project Manager', accountable: 'Sponsor', informed: 'Steering Committee', due_date: '2025-01-17', status: 'In Progress' }
    ],
    followups: [
      { item: 'Schedule load testing', owner: 'QA Lead', due_date: '2025-02-01', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Issue & Risk Register Draft";
      const payload = {
        name: draftName,
        template_type: "Issue & Risk Register",
        template_id: "issue-risk-register",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "issue-risk-register", "Issue Risk Register", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("issue-risk-register", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/issue-risk-register`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Issue_Risk_Register_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Issue & Risk Register",
        formData,
        settings: activeSettings || {},
        templateType: "Issue & Risk Register",
        templateId: "issue-risk-register",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addIssue = () => {
    const newId = `ISS-${String(formData.issues.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      issues: [...formData.issues, { id: newId, title: '', description: '', category: '', priority: 'Medium', status: 'Open', owner: '', reported_date: new Date().toISOString().split('T')[0], target_date: '', resolution: '', impact: '', notes: '' }]
    });
  };

  const addRisk = () => {
    const newId = `RISK-${String(formData.risks.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      risks: [...formData.risks, { id: newId, title: '', description: '', category: '', likelihood: 'Medium', impact: 'Medium', risk_score: 0, status: 'Open', owner: '', mitigation: '', contingency: '', trigger: '', notes: '' }]
    });
  };

  const updateIssue = (index, field, value) => {
    const issues = [...formData.issues];
    issues[index] = { ...issues[index], [field]: value };
    setFormData({ ...formData, issues });
  };

  const updateRisk = (index, field, value) => {
    const risks = [...formData.risks];
    risks[index] = { ...risks[index], [field]: value };
    // Calculate risk score
    if (field === 'likelihood' || field === 'impact') {
      const likelihoodScores = { 'Low': 1, 'Medium': 2, 'High': 3, 'Critical': 4 };
      const impactScores = { 'Low': 1, 'Medium': 2, 'High': 3, 'Critical': 4 };
      risks[index].risk_score = (likelihoodScores[risks[index].likelihood] || 0) * (impactScores[risks[index].impact] || 0);
    }
    setFormData({ ...formData, risks });
  };

  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const issueStatuses = ['Open', 'In Progress', 'Blocked', 'Resolved', 'Closed'];
  const riskStatuses = ['Open', 'Active', 'Monitoring', 'Mitigated', 'Closed', 'Occurred'];
  const likelihoods = ['Low', 'Medium', 'High', 'Critical'];
  const impacts = ['Low', 'Medium', 'High', 'Critical'];
  const categories = ['Technical', 'Resource', 'Scope', 'Schedule', 'Budget', 'Data', 'Compliance', 'External', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Issue & Risk Register</h1><p className="text-sm text-gray-500">Issue and risk tracking</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Issue & Risk Register</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Name" /></div>
            </div>

            <Separator />

            <Tabs defaultValue="issues">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="issues">Issues ({formData.issues.length})</TabsTrigger>
                <TabsTrigger value="risks">Risks ({formData.risks.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="issues" className="mt-4">
                <div className="space-y-3">
                  {formData.issues.map((issue, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${issue.status === 'Resolved' || issue.status === 'Closed' ? 'bg-green-50' : issue.priority === 'Critical' ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                        <div><Label className="text-xs">ID</Label><Input value={issue.id} onChange={(e) => updateIssue(idx, 'id', e.target.value)} className="text-sm font-mono" /></div>
                        <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={issue.title} onChange={(e) => updateIssue(idx, 'title', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Priority</Label>
                          <Select value={issue.priority} onValueChange={(v) => updateIssue(idx, 'priority', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Status</Label>
                          <Select value={issue.status} onValueChange={(v) => updateIssue(idx, 'status', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{issueStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end"><Button variant="ghost" size="icon" onClick={() => setFormData({...formData, issues: formData.issues.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        <div><Label className="text-xs">Owner</Label><Input value={issue.owner} onChange={(e) => updateIssue(idx, 'owner', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Category</Label>
                          <Select value={issue.category} onValueChange={(v) => updateIssue(idx, 'category', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Reported</Label><Input type="date" value={issue.reported_date} onChange={(e) => updateIssue(idx, 'reported_date', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Target</Label><Input type="date" value={issue.target_date} onChange={(e) => updateIssue(idx, 'target_date', e.target.value)} className="text-sm" /></div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addIssue} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Issue</Button>
              </TabsContent>

              <TabsContent value="risks" className="mt-4">
                <div className="space-y-3">
                  {formData.risks.map((risk, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${risk.risk_score >= 12 ? 'bg-red-50' : risk.risk_score >= 6 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                        <div><Label className="text-xs">ID</Label><Input value={risk.id} onChange={(e) => updateRisk(idx, 'id', e.target.value)} className="text-sm font-mono" /></div>
                        <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={risk.title} onChange={(e) => updateRisk(idx, 'title', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Likelihood</Label>
                          <Select value={risk.likelihood} onValueChange={(v) => updateRisk(idx, 'likelihood', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{likelihoods.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Impact</Label>
                          <Select value={risk.impact} onValueChange={(v) => updateRisk(idx, 'impact', v)}>
                            <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{impacts.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Score</Label><div className={`text-sm font-bold py-2 px-2 rounded ${risk.risk_score >= 12 ? 'bg-red-200' : risk.risk_score >= 6 ? 'bg-yellow-200' : 'bg-green-200'}`}>{risk.risk_score}</div></div>
                        <div className="flex items-end"><Button variant="ghost" size="icon" onClick={() => setFormData({...formData, risks: formData.risks.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                        <div><Label className="text-xs">Mitigation</Label><Input value={risk.mitigation} onChange={(e) => updateRisk(idx, 'mitigation', e.target.value)} placeholder="Risk mitigation strategy" className="text-sm" /></div>
                        <div><Label className="text-xs">Owner</Label><Input value={risk.owner} onChange={(e) => updateRisk(idx, 'owner', e.target.value)} className="text-sm" /></div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addRisk} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Risk</Button>
              </TabsContent>
            </Tabs>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default IssueRiskRegisterForm;
